import React from "react";
import { H2, IconButton, SystemMessage, Text, Tooltip } from "@shoptet/ui";
import { ArcElement, Chart as ChartJS, Tooltip as ChartTooltip, Legend, TooltipItem, TooltipModel } from "chart.js";
import ChartDataLabels, { Context } from "chartjs-plugin-datalabels";
import { Pie } from "react-chartjs-2";
import { Trans, useTranslation } from "react-i18next";
import { PIE_COLORS } from "../../constants/chartColors";
import { AdSystemT, useProductExclusionQuery } from "../../graphql/generated/graphql";
import "./ProductsDashboard.css";

ChartJS.register(ArcElement, ChartTooltip, Legend, ChartDataLabels);

const TOOLTIP_ID = "products-pie-tooltip";

const getOrCreateTooltip = () => {
  let tooltipEl = document.getElementById(TOOLTIP_ID);

  if (!tooltipEl) {
    tooltipEl = document.createElement("div");
    tooltipEl.id = TOOLTIP_ID;
    tooltipEl.style.cssText =
      "background:rgba(0,0,0,.8);border-radius:4px;color:var(--app-text-on-accent);opacity:0;pointer-events:none;" +
      "position:fixed;transition:opacity .15s;font-size:12px;padding:8px 12px;white-space:nowrap;z-index:9999";
    document.body.appendChild(tooltipEl);
  }

  return tooltipEl;
};

const externalTooltipHandler = (context: { chart: ChartJS; tooltip: TooltipModel<"pie"> }) => {
  const { chart, tooltip } = context;
  const tooltipEl = getOrCreateTooltip();

  if (tooltip.opacity === 0) {
    tooltipEl.style.opacity = "0";
    return;
  }

  if (tooltip.body) {
    const lines = tooltip.body.flatMap((b) => b.lines).filter(Boolean);
    tooltipEl.innerHTML = lines.join("<br>");
  }

  const rect = chart.canvas.getBoundingClientRect();
  tooltipEl.style.opacity = "1";
  tooltipEl.style.left = rect.left + tooltip.caretX + "px";
  tooltipEl.style.top = rect.top + tooltip.caretY + "px";
  tooltipEl.style.transform = "translate(10px, -50%)";
};

interface ProductsDashboardProps {
  adSystem?: AdSystemT;
  isWizard?: boolean;
}

const calculatePercent = (count: number, total: number, addPercentSign = false) => {
  const value = total > 0 ? Math.round((count / total) * 100) : 0;
  return addPercentSign ? `${value}%` : value;
};

const ProductsDashboard: React.FC<ProductsDashboardProps> = ({ adSystem = AdSystemT.GoogleAdsT, isWizard = false }) => {
  const { t } = useTranslation("translation", { keyPrefix: "Components.ProductsDashboard" });
  const { data } = useProductExclusionQuery({
    variables: { adSystem },
  });
  const totalProductsCount = data?.organization?.productsCount || 0;
  const excludedProductsCount = data?.organization?.excludedProductsCount || 0;
  const excludedProductByFlagsCount = data?.organization?.excludedProductFlagsProductsCount || 0;
  const excludedProductByCategoriesCount = data?.organization?.excludedCategoriesProductsCount || 0;
  const outOfStockProductsCount = data?.organization?.outOfStockProductsCount || 0;
  const bannedProductsCount = data?.organization?.bannedProductsCount || 0;
  const totalNotExcludedProductsCount = totalProductsCount - excludedProductsCount;

  const pieSegments = [
    { value: totalNotExcludedProductsCount, color: PIE_COLORS.included, label: t("Products included in campaigns") },
    { value: excludedProductsCount, color: PIE_COLORS.excluded, label: t("Products excluded from campaigns") },
  ].filter((seg) => seg.value > 0);

  const pieData = {
    labels: pieSegments.map((seg) => seg.label),
    datasets: [
      {
        data: pieSegments.map((seg) => seg.value),
        backgroundColor: pieSegments.map((seg) => seg.color),
        offset: 2,
      },
    ],
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: true,
    layout: {
      padding: 10,
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
        external: externalTooltipHandler,
        callbacks: {
          title: () => [],
          label: (context: TooltipItem<"pie">) => {
            const pct = (count: number) => calculatePercent(count, totalProductsCount, true);
            const isExcludedSegment = pieSegments[context.dataIndex]?.color === PIE_COLORS.excluded;
            if (!isExcludedSegment) {
              return `${pct(totalNotExcludedProductsCount)} ${t("Tooltip - products in campaigns")}`;
            }
            return [
              outOfStockProductsCount > 0 && `${pct(outOfStockProductsCount)} ${t("Tooltip - out of stock")}`,
              bannedProductsCount > 0 && `${pct(bannedProductsCount)} ${t("Tooltip - banned products")}`,
              excludedProductByCategoriesCount > 0 &&
                `${pct(excludedProductByCategoriesCount)} ${t("Tooltip - excluded by category")}`,
              excludedProductByFlagsCount > 0 &&
                `${pct(excludedProductByFlagsCount)} ${t("Tooltip - excluded by tags")}`,
            ].filter(Boolean) as string[];
          },
        },
      },
      datalabels: {
        formatter: (value: number, context: Context) => {
          const total = context.chart.data.datasets[0].data.reduce(
            (a, b) => (a as number) + (b as number),
            0
          ) as number;
          const percentage = Math.round((value / total) * 100);
          return `${percentage}%`;
        },
        color: PIE_COLORS.label,
        font: {
          weight: "bold" as const,
          size: 12,
        },
      },
    },
  };

  return (
    <div>
      {data && (
        <>
          <div style={{ marginBottom: "16px" }}>
            <div className="ProductsDashboard-Columns">
              <div
                className="ProductsDashboard-LeftHeader"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div className="typography-no-margin" style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <H2>{t("Products in campaigns")}</H2>
                  <Tooltip
                    closeDelay={300}
                    delay={500}
                    content={t("Products in campaigns tooltip", {
                      adSystem: adSystem === AdSystemT.GoogleAdsT ? "Google" : "Meta",
                    })}
                  >
                    <IconButton icon="shp-info" ghost />
                  </Tooltip>
                </div>
                <div>
                  <Text appearance="strong-lg">
                    {calculatePercent(totalNotExcludedProductsCount, totalProductsCount, true)}
                  </Text>
                  <Text>{` (${totalNotExcludedProductsCount} ${t("products")})`}</Text>
                </div>
              </div>
              <div
                className="ProductsDashboard-LeftContent"
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  border: "1px solid var(--app-border)",
                  borderRadius: "var(--app-radius-box)",
                  padding: "37px 16px",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "67px" }}>
                  <div style={{ width: "160px", height: "160px" }}>
                    <Pie data={pieData} options={pieOptions} />
                  </div>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
                      <div
                        style={{ width: "8px", height: "8px", background: PIE_COLORS.included, borderRadius: "50%" }}
                      />
                      <Text appearance="strong-md">{t("Products included in campaigns")}</Text>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <div
                        style={{ width: "8px", height: "8px", background: PIE_COLORS.excluded, borderRadius: "50%" }}
                      />
                      <Text appearance="strong-md">{t("Products excluded from campaigns")}</Text>
                    </div>
                    <div style={{ marginLeft: "16px", marginTop: "4px" }}>
                      {outOfStockProductsCount > 0 && (
                        <div>
                          <Text variant="secondary">
                            {t("Tooltip - out of stock")} (
                            {calculatePercent(outOfStockProductsCount, totalProductsCount, true)})
                          </Text>
                        </div>
                      )}
                      {bannedProductsCount > 0 && (
                        <div>
                          <Text variant="secondary">
                            {t("Tooltip - banned products")} (
                            {calculatePercent(bannedProductsCount, totalProductsCount, true)})
                          </Text>
                        </div>
                      )}
                      {excludedProductByFlagsCount > 0 && (
                        <div>
                          <Text variant="secondary">
                            {t("Tooltip - excluded by tags")} (
                            {calculatePercent(excludedProductByFlagsCount, totalProductsCount, true)})
                          </Text>
                        </div>
                      )}
                      {excludedProductByCategoriesCount > 0 && (
                        <div>
                          <Text variant="secondary">
                            {t("Tooltip - excluded by category")} (
                            {calculatePercent(excludedProductByCategoriesCount, totalProductsCount, true)})
                          </Text>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="ProductsDashboard-RightHeader">
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "4px",
                  }}
                >
                  <div className="typography-no-margin" style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    <H2>{t("Excluded products")}</H2>
                    <Tooltip closeDelay={300} content={t("Excluded products tooltip")} delay={500}>
                      <IconButton icon="shp-info" ghost />
                    </Tooltip>
                  </div>
                  <div data-test-id="excluded-products-total">
                    <Text appearance="strong-lg">
                      {calculatePercent(excludedProductsCount, totalProductsCount, true)}
                    </Text>
                    <Text>{` (${excludedProductsCount} ${t("products")})`}</Text>
                  </div>
                </div>
                <div>
                  <Text>{t("Excluded products can appear in multiple categories")}</Text>
                </div>
              </div>
              <div className="ProductsDashboard-RightContent">
                <div style={{ display: "flex", gap: "16px", marginBottom: "16px" }}>
                  <div
                    style={{
                      padding: "21px 16px",
                      border: "1px solid var(--app-border)",
                      borderRadius: "var(--app-radius-box)",
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    <div style={{ marginBottom: "8px" }}>
                      <Text>{t("Out of stock (automatically excluded)")}</Text>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <div>
                        <div>
                          <Text appearance="strong-lg" variant="secondary">
                            {calculatePercent(outOfStockProductsCount, totalProductsCount, true)}
                          </Text>
                          <Text variant="secondary">{` (${outOfStockProductsCount})`}</Text>
                        </div>
                        <Text variant="secondary">{t("all products")}</Text>
                      </div>
                      <div>
                        <Tooltip
                          closeDelay={300}
                          content={t("Out of stock (automatically excluded) tooltip")}
                          delay={500}
                          placement="left"
                        >
                          <IconButton icon="shp-info" ghost />
                        </Tooltip>
                      </div>
                    </div>
                  </div>
                  <div
                    style={{
                      padding: "21px 16px",
                      border: "1px solid var(--app-border)",
                      borderRadius: "var(--app-radius-box)",
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    <div style={{ marginBottom: "8px" }}>
                      <Text>{t("Banned products")}</Text>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <div>
                        <div>
                          <Text appearance="strong-lg" data-test-id="banned-products-count" variant="secondary">
                            {calculatePercent(bannedProductsCount, totalProductsCount, true)}
                          </Text>
                          <Text variant="secondary">{` (${bannedProductsCount})`}</Text>
                        </div>
                        <Text variant="secondary">{t("all products")}</Text>
                      </div>
                      <div>
                        <Tooltip
                          closeDelay={300}
                          delay={500}
                          placement="left"
                          content={t("Banned products tooltip", {
                            adSystem: adSystem === AdSystemT.GoogleAdsT ? "Google" : "Meta",
                          })}
                        >
                          <IconButton icon="shp-info" ghost />
                        </Tooltip>
                      </div>
                    </div>
                  </div>
                </div>
                <div style={{ display: "flex", gap: "16px" }}>
                  <div
                    style={{
                      padding: "21px 16px",
                      border: "1px solid var(--app-border)",
                      borderRadius: "var(--app-radius-box)",
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    <div style={{ marginBottom: "8px" }}>
                      <Text>{t("Excluded by flag")}</Text>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <div>
                        <div>
                          <Text
                            appearance="strong-lg"
                            data-test-id="excluded-products-by-product-flags"
                            variant="secondary"
                          >
                            {calculatePercent(excludedProductByFlagsCount, totalProductsCount, true)}
                          </Text>
                          <Text variant="secondary">{` (${excludedProductByFlagsCount})`}</Text>
                        </div>
                        <Text variant="secondary">{t("all products")}</Text>
                      </div>
                      <div>
                        <Tooltip closeDelay={300} content={t("Excluded by flag tooltip")} delay={500} placement="left">
                          <IconButton icon="shp-info" ghost />
                        </Tooltip>
                      </div>
                    </div>
                  </div>
                  <div
                    style={{
                      padding: "21px 16px",
                      border: "1px solid var(--app-border)",
                      borderRadius: "var(--app-radius-box)",
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    <div style={{ marginBottom: "8px" }}>
                      <Text>{t("Excluded by selected categories")}</Text>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <div>
                        <div>
                          <Text
                            appearance="strong-lg"
                            data-test-id="excluded-products-by-categories"
                            variant="secondary"
                          >
                            {calculatePercent(excludedProductByCategoriesCount, totalProductsCount, true)}
                          </Text>
                          <Text variant="secondary">{` (${excludedProductByCategoriesCount})`}</Text>
                        </div>
                        <Text variant="secondary">{t("all products")}</Text>
                      </div>
                      <div>
                        <Tooltip
                          closeDelay={300}
                          content={t("Excluded by selected categories tooltip")}
                          delay={500}
                          placement="left"
                        >
                          <IconButton icon="shp-info" ghost />
                        </Tooltip>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {totalProductsCount > 0 && (bannedProductsCount / totalProductsCount) * 100 > 90 && (
            <div style={{ marginBottom: "16px" }}>
              <SystemMessage
                level="alert"
                title={t("Banned products warning title")}
                description={
                  <Text>
                    <Trans
                      components={{ a: <a href="mailto:info@shoptet.cz" /> }}
                      i18nKey="Banned products warning description"
                      t={t}
                    />
                  </Text>
                }
              />
            </div>
          )}
          {isWizard && (
            <div style={{ marginBottom: "32px" }}>
              <SystemMessage
                level="notice"
                description={
                  <Text>
                    <Trans components={{ b: <b /> }} i18nKey="Info - description" t={t} />
                  </Text>
                }
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ProductsDashboard;
